/**
 * Port of the `@functions` block in the Header view component
 * (`Views/Shared/Components/Header/Default.cshtml`, v2.4.2): the culture → flag
 * and culture → native-name tables the language picker renders.
 *
 * The tables are reproduced in full even though only four cultures are
 * configured today, so adding one to `Application:SupportedCultures` keeps
 * working without a frontend change — exactly as it did in Razor.
 */

/** `"en-GB"` → `"en"`. */
export function baseLanguage(culture: string): string {
  if (!culture.trim()) return 'en';
  return culture.split('-')[0].toLowerCase();
}

/**
 * flag-icons class for a culture. The region is honoured first, which is how
 * both en-GB and en-US end up on the Union Jack rather than two different flags.
 */
export function flagClass(culture: string): string {
  if (!culture.trim()) return 'fi fi-gb';

  const parts = culture.split('-').filter(Boolean);

  if (parts.length > 1) {
    const region = parts[1].toLowerCase();
    if (REGION_FLAGS[region]) return REGION_FLAGS[region];
  }

  return LANGUAGE_FLAGS[baseLanguage(culture)] ?? 'fi fi-gb';
}

/** Native name of the language, for the picker label. */
export function languageName(culture: string): string {
  if (!culture.trim()) return 'English';
  return LANGUAGE_NAMES[baseLanguage(culture)] ?? culture.toUpperCase();
}

/** Regions with an explicit flag, from `getFlagClass`. */
const REGION_FLAGS: Record<string, string> = {
  gb: 'fi fi-gb',
  uk: 'fi fi-gb',
  // Deliberate: the design shows the Union Jack for English everywhere.
  us: 'fi fi-gb',
  it: 'fi fi-it',
  fr: 'fi fi-fr',
  es: 'fi fi-es',
  de: 'fi fi-de',
};

const LANGUAGE_FLAGS: Record<string, string> = {
  en: 'fi fi-gb',
  it: 'fi fi-it',
  fr: 'fi fi-fr',
  es: 'fi fi-es',
  de: 'fi fi-de',
  pt: 'fi fi-pt',
  nl: 'fi fi-nl',
  pl: 'fi fi-pl',
  ru: 'fi fi-ru',
  zh: 'fi fi-cn',
  ja: 'fi fi-jp',
  ko: 'fi fi-kr',
  ar: 'fi fi-sa',
  tr: 'fi fi-tr',
  cs: 'fi fi-cz',
  el: 'fi fi-gr',
  he: 'fi fi-il',
  hi: 'fi fi-in',
  hu: 'fi fi-hu',
  id: 'fi fi-id',
  ro: 'fi fi-ro',
  sk: 'fi fi-sk',
  sv: 'fi fi-se',
  th: 'fi fi-th',
  uk: 'fi fi-ua',
  vi: 'fi fi-vn',
};

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  it: 'Italiano',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
  pt: 'Português',
  nl: 'Nederlands',
  pl: 'Polski',
  ru: 'Русский',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  ar: 'العربية',
  tr: 'Türkçe',
  cs: 'Čeština',
  el: 'Ελληνικά',
  he: 'עברית',
  hi: 'हिन्दी',
  hu: 'Magyar',
  id: 'Bahasa Indonesia',
  ro: 'Română',
  sk: 'Slovenčina',
  sv: 'Svenska',
  th: 'ไทย',
  uk: 'Українська',
  vi: 'Tiếng Việt',
};
